import React, { useEffect, useRef, useState } from 'react';
import { Check, Star } from 'lucide-react';

type Size = 'sm' | 'md' | 'lg';

interface Props {
  code: string;
  count: number;
  size?: Size;
  showCode?: boolean;
  onClick?: () => void;
  onLongPress?: () => void;
  isPriority?: boolean;
  priorityRank?: number;
  onTogglePriority?: () => void;
}

const SIZE_PX: Record<Size, number> = { sm: 56, md: 64, lg: 72 };
const NUM_FONT: Record<Size, string> = { sm: 'text-[16px]', md: 'text-[18px]', lg: 'text-[20px]' };
const LONG_PRESS_MS = 500;
const WIGGLE_MS = 320;

export function StickerCard({
  code,
  count,
  size = 'md',
  showCode = false,
  onClick,
  onLongPress,
  isPriority = false,
  priorityRank,
  onTogglePriority,
}: Props) {
  const num = code.replace(/^[A-Z_]+/, '');
  const prefix = code.match(/^[A-Z_]+/)?.[0] || '';
  const isLabelOnly = num === '';
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const longPressFiredRef = useRef(false);
  const activePointerIdRef = useRef<number | null>(null);
  const prevCountRef = useRef(count);
  const [stamp, setStamp] = useState(false);
  const [pressing, setPressing] = useState(false);
  const [wiggle, setWiggle] = useState(false);

  useEffect(() => {
    if (count > prevCountRef.current) {
      setStamp(true);
      const t = setTimeout(() => setStamp(false), 300);
      prevCountRef.current = count;
      return () => clearTimeout(t);
    }
    prevCountRef.current = count;
  }, [count]);

  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  const clearTimer = () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  };

  const handlePointerDown = (e: React.PointerEvent<HTMLButtonElement>) => {
    if (activePointerIdRef.current !== null) return;
    activePointerIdRef.current = e.pointerId;
    longPressFiredRef.current = false;
    setPressing(true);
    clearTimer();
    timerRef.current = setTimeout(() => {
      longPressFiredRef.current = true;
      setPressing(false);
      setWiggle(true);
      setTimeout(() => setWiggle(false), WIGGLE_MS);
      if (typeof navigator !== 'undefined' && typeof navigator.vibrate === 'function') {
        navigator.vibrate(40);
      }
      onLongPress?.();
    }, LONG_PRESS_MS);
  };

  const endPointer = (e: React.PointerEvent<HTMLButtonElement>) => {
    if (activePointerIdRef.current !== e.pointerId) return;
    activePointerIdRef.current = null;
    setPressing(false);
    clearTimer();
    if (longPressFiredRef.current) {
      e.preventDefault();
    }
  };

  const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (longPressFiredRef.current) {
      longPressFiredRef.current = false;
      e.preventDefault();
      return;
    }
    onClick?.();
  };

  const isMissing = count === 0;
  const isOwned = count === 1;
  const isDup = count >= 2;

  const stateClasses = isOwned
    ? 'bg-green-700 text-white border border-green-900 shadow-sm'
    : isDup
    ? 'bg-gold-500 text-ink-900 border border-gold-600 shadow-glow-gold'
    : 'bg-white text-ink-500 border border-ink-300';

  const dim = SIZE_PX[size];

  const ariaLabel = isMissing
    ? `Figurinha ${code}, faltante. Clique para marcar como tendo.`
    : isOwned
    ? `Figurinha ${code}, você tem 1. Clique para marcar como repetida. Pressione e segure para resetar.`
    : `Figurinha ${code}, você tem ${count}. Clique para incrementar. Pressione e segure para resetar.`;

  const motionClass = wiggle
    ? 'animate-wiggle'
    : stamp
    ? 'animate-stamp'
    : pressing
    ? 'scale-110'
    : 'scale-100';

  return (
    <button
      type="button"
      onClick={handleClick}
      onPointerDown={handlePointerDown}
      onPointerUp={endPointer}
      onPointerCancel={endPointer}
      onPointerLeave={endPointer}
      onContextMenu={(e) => e.preventDefault()}
      aria-label={ariaLabel}
      style={{ width: dim, height: dim, touchAction: 'manipulation' }}
      className={[
        'relative flex flex-col items-center justify-center rounded-lg font-mono select-none cursor-pointer p-0',
        'transition duration-fast ease-out btn-focus',
        stateClasses,
        motionClass,
      ].join(' ')}
    >
      {isDup && (
        <span
          aria-hidden="true"
          className="absolute inset-0 rounded-[inherit] pointer-events-none"
          style={{
            background:
              'linear-gradient(135deg, rgba(255,255,255,0.45) 0%, rgba(255,255,255,0) 38%, rgba(255,255,255,0) 62%, rgba(255,255,255,0.25) 100%)',
          }}
        />
      )}

      {showCode && prefix && !isLabelOnly && (
        <span className="text-[8px] font-semibold opacity-60 tracking-[0.08em] mb-px">
          {prefix}
        </span>
      )}
      {isLabelOnly ? (
        <span className="font-bold leading-none text-[11px] tracking-[0.05em]">{prefix}</span>
      ) : (
        <span className={`font-bold leading-none tabular-nums ${NUM_FONT[size]}`}>{num}</span>
      )}

      {isOwned && (
        <span
          aria-hidden="true"
          className="absolute top-[5px] right-[5px] w-[14px] h-[14px] rounded-full bg-white/20 flex items-center justify-center"
        >
          <Check size={10} strokeWidth={3} className="text-white" />
        </span>
      )}

      {onTogglePriority && (
        <span
          role="button"
          tabIndex={0}
          aria-label={isPriority ? 'Remover das prioridades' : 'Marcar como prioridade'}
          aria-pressed={isPriority}
          onPointerDown={(e) => {
            e.stopPropagation();
          }}
          onClick={(e) => {
            e.stopPropagation();
            onTogglePriority();
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              e.stopPropagation();
              onTogglePriority();
            }
          }}
          className={[
            'absolute -top-1 -left-1 w-[22px] h-[22px] rounded-full flex items-center justify-center cursor-pointer',
            'border-2 border-paper transition-colors duration-fast',
            isPriority
              ? 'bg-gold-500 text-ink-900 shadow-glow-gold'
              : 'bg-white text-ink-500 hover:bg-gold-100 hover:text-gold-600 border-ink-300',
          ].join(' ')}
          style={{ touchAction: 'manipulation' }}
        >
          <Star
            size={11}
            strokeWidth={2.5}
            fill={isPriority ? 'currentColor' : 'none'}
          />
          {isPriority && priorityRank && (
            <span
              aria-hidden="true"
              className="absolute -bottom-1.5 -right-1.5 min-w-[14px] h-[14px] px-0.5 rounded-full bg-green-700 text-white font-bold text-[8px] flex items-center justify-center border border-paper"
            >
              {priorityRank}
            </span>
          )}
        </span>
      )}

      {isDup && (
        <span
          aria-hidden="true"
          className="absolute -top-1.5 -right-1.5 min-w-[22px] h-[22px] px-1.5 rounded-pill bg-green-700 text-white font-mono font-bold text-[11px] flex items-center justify-center shadow-sm border-2 border-paper"
        >
          x{count}
        </span>
      )}
    </button>
  );
}
